import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { ArrowBackIosNew as BackIcon, KeyboardDoubleArrowRight as DoubleArrowIcon, BeachAccess as TotalIcon, EventAvailable as RemainingIcon, Pending as PendingIcon, Close as CloseIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { getData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';
import Loader from '../../common/spinner';
import MyLeaveRequests from './myLeaveRequests';
import HolidayCalendar from './holidayCalendar';
import LeaveBalanceSummary from './leaveBalanceSummary';
import LeaveApplicationForm from './leaveApplicationForm';

function LeaveRequestsManager() {
    const location = useLocation();
    const [leaveData, setLeaveData] = useState({
        total_leaves: "0",
        remaining_leaves: "0",
        pending_leaves: "0"
    });
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(null);
    const menuButtons = ["My Leaves", "Apply for Leave", "Holidays List", "Leave Balance"];
    const isHide = location.pathname === '/employee/manage-leave-requests' || location.pathname === '/hr/leave-management'
    const handleOpenDialog = (type) => setOpenDialog(type);
    const handleCloseDialog = () => setOpenDialog(null);

    useEffect(() => {
        const fetchLeaveBalance = async () => {
            try {
                const raw = localStorage.getItem('userData');
                const userData = raw ? JSON.parse(raw) : null;
                const userId = userData?.id;

                if (userId) {
                    const response = await getData(API_ENDPOINTS.getEmployeeLeaveBalance(userId));
                    if (response.status === "SUCCESS") {
                        setLeaveData(response.message);
                    }
                }
            } catch (error) {
                return null;
            } finally {
                setLoading(false);
            }
        };

        fetchLeaveBalance();
    }, []);

    const statsConfig = [
        {
            title: "Total Leaves",
            value: leaveData.total_leaves || 0,
            icon: <TotalIcon />,
            color: "#6366F1", // Indigo
            gradient: "linear-gradient(135deg, #6366F1 0%, #4338CA 100%)"
        },
        {
            title: "Remaining Leaves",
            value: leaveData.remaining_leaves || 0,
            icon: <RemainingIcon />,
            color: "#10B981", // Green
            gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)"
        },
        {
            title: "Pending Requests",
            value: leaveData.pending_leaves || 0,
            icon: <PendingIcon />,
            color: "#F59E0B", // Amber
            gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
        },
    ];

    const renderDialogContent = (onClose) => {
        switch (openDialog) {
            case "My Leaves": return <MyLeaveRequests onClose={onClose} />;
            case "Apply for Leave": return <LeaveApplicationForm onClose={onClose} />;
            case "Holidays List": return <HolidayCalendar onClose={onClose} />;
            case "Leave Balance": return <LeaveBalanceSummary onClose={onClose} />;
            default: return null;
        }
    };

    const getDialogCaption = (type) => {
        switch (type) {
            case "My Leaves": return "View and track your previous leave history.";
            case "Apply for Leave": return "Fill out the details below to request time off.";
            case "Holidays List": return "Upcoming company-wide holidays and observances.";
            case "Leave Balance": return "Detailed breakdown of your available leave types.";
            default: return "";
        }
    };

    if (loading) return <Loader />;

    return (
        <Box sx={{ p: 1 }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                {!isHide && (
                    <Tooltip title="Go Back" arrow>
                        <IconButton sx={{ mr: 1 }} onClick={() => window.history.back()}>
                            <BackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Typography variant="h6" fontWeight="bold">
                    Leave Requests
                </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 4 }}>
                {statsConfig.map((stat, index) => (
                    <Grid size={{ xs: 12, sm: 4 }} key={index}>
                        <Card sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            background: stat.gradient,
                            borderRadius: 4,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            color: "white",
                            minHeight: '130px',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <CardContent sx={{ width: '100%', position: 'relative', zIndex: 1, px: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, opacity: 0.8, letterSpacing: '0.5px' }}>
                                            {stat.title}
                                        </Typography>
                                        <Typography variant="h3" fontWeight="800" sx={{ mt: 0.5 }}>
                                            {stat.value}
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: '12px',
                                        p: 1.5,
                                        display: 'flex',
                                        backdropFilter: 'blur(4px)'
                                    }}>
                                        {React.cloneElement(stat.icon, { sx: { color: '#fff', fontSize: 28 } })}
                                    </Box>
                                </Box>
                            </CardContent>

                            {/* Background Decorative Circle */}
                            <Box sx={{
                                position: 'absolute',
                                top: '-18%',
                                right: '-5%',
                                width: '140px',
                                height: '140px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.1)',
                                zIndex: 0
                            }} />
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Navigation Buttons */}
            <Grid container spacing={2}>
                {menuButtons.map((text, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => handleOpenDialog(text)}
                            endIcon={<DoubleArrowIcon />}
                            sx={{
                                justifyContent: "space-between",
                                py: 2.5,
                                px: 3,
                                borderRadius: 3,
                                textTransform: "none",
                                fontSize: "1rem",
                                borderColor: "#f0f0f0",
                                color: "#333",
                                fontWeight: 500,
                                backgroundColor: "#fff",
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    borderColor: "primary.main",
                                    backgroundColor: "#fff",
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                    transform: 'translateY(-2px)'
                                },
                                '& .MuiButton-endIcon': {
                                    color: 'primary.main'
                                }
                            }}
                        >
                            {text}
                        </Button>
                    </Grid>
                ))}
            </Grid>

            {/* Global Dialog for all Leave Actions */}
            <Dialog
                open={Boolean(openDialog)}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
                disableEnforceFocus
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 0.8 }}>
                            {openDialog}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'normal' }}>
                            {getDialogCaption(openDialog)}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseDialog} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {renderDialogContent(handleCloseDialog)}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default LeaveRequestsManager;